import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Select,
  Tooltip,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import {
  ContactSubjectType,
  EmailInviteLanguage,
  MinimalOrgDto,
  useGetOrganizationQuery,
  useListContactPersonsQuery,
  useListPersonsQuery,
} from "../../../../../../autogen/bff-api";
import { supplierContactPersons } from "../../../../../../common/contactPersons/supplierContactPersons";
import { ModalSkeleton } from "../../../../../../common/ModalSkeleton";
import { Paging } from "../../../../../../common/Paging";
import { displayPersonNameWithEmail } from "../../../../../contracts/view-single/sharing/AddExternalParticipantModal";

const limit = 10;

export interface OrganizationInviteRecipients {
  personIds: string[];
  emailInvites: { email: string; language: EmailInviteLanguage }[];
}

interface ManualEmailInvite {
  id: string;
  email: string;
  language: EmailInviteLanguage;
}

export const V2InviteOrganizationModal = ({
  minimalOrg,
  onClose,
  onCreate,
  showPublishedWarning = false,
}: {
  minimalOrg?: MinimalOrgDto;
  onClose: () => void;
  onCreate: (recipients: OrganizationInviteRecipients) => void;
  showPublishedWarning?: boolean;
}) => {
  const { t } = useTranslation();
  const [skip, setSkip] = useState(0);
  const { data, isLoading, isFetching } = useListPersonsQuery(
    minimalOrg ? { orgIds: [minimalOrg.id], limit, skip } : skipToken,
  );
  const { data: organizationProfile, isLoading: isLoadingOrgProfile } = useGetOrganizationQuery(
    minimalOrg?.id ? { orgId: minimalOrg.id } : skipToken,
  );
  const { data: orgContacts, isLoading: isLoadingOrgContacts } = useListContactPersonsQuery(
    minimalOrg?.id
      ? { subjectType: ContactSubjectType.RegisteredOrganization, subjectId: minimalOrg.id }
      : skipToken,
  );

  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  const [selectedContactPersonEmails, setSelectedContactPersonEmails] = useState<string[]>([]);
  const [manualEmailInvites, setManualEmailInvites] = useState<ManualEmailInvite[]>([]);

  const contactPersons = supplierContactPersons(orgContacts?.contacts ?? [], organizationProfile?.linkedEntries ?? []);

  const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const areManualEmailsValid = manualEmailInvites.every((invite) => isValidEmail(invite.email.trim()));

  const hasAnySelection =
    selectedPersonIds.length > 0 ||
    selectedContactPersonEmails.length > 0 ||
    (manualEmailInvites.length > 0 && areManualEmailsValid);
  const isCreateDisabled = !hasAnySelection || (manualEmailInvites.length > 0 && !areManualEmailsValid);

  const tooltipMessage = !hasAnySelection
    ? t("Select at least one person or add a valid email invite")
    : t("All email addresses must be valid");

  const handleClose = () => {
    setSelectedPersonIds([]);
    setSelectedContactPersonEmails([]);
    setManualEmailInvites([]);
    setSkip(0);
    onClose();
  };

  const createInvite = () => {
    onCreate({
      personIds: selectedPersonIds,
      emailInvites: [
        ...contactPersons
          .filter((p) => selectedContactPersonEmails.includes(p.email))
          .map((p) => ({ email: p.email, language: EmailInviteLanguage.No })),
        ...manualEmailInvites.map((invite) => ({ email: invite.email.trim(), language: invite.language })),
      ],
    });
    handleClose();
  };

  const isLoadingAnything = isLoading || isFetching || isLoadingOrgProfile || isLoadingOrgContacts;

  return (
    <Modal isOpen={!!minimalOrg} onClose={handleClose} size="3xl">
      <ModalOverlay />
      <ModalContent width="full" maxHeight="80%" padding="5" overflow="hidden">
        <ModalCloseButton />
        <ModalBody height="full" overflowY="auto">
          <Flex height="full" flexDirection="column">
            <Heading as="h2" size="md" pb="2">
              {t("Select persons to invite for")} {minimalOrg?.name}
            </Heading>
            {isLoadingAnything && <ModalSkeleton />}
            {!isLoadingAnything && minimalOrg && data && (
              <Flex flexDirection="column" pt="5">
                <FormLabel fontSize="sm">{t("Persons with a SourceMagnet account")}</FormLabel>
                <CheckboxGroup colorScheme="teal">
                  {data.persons.map((p) => (
                    <Checkbox
                      key={p.id}
                      isChecked={selectedPersonIds.includes(p.id)}
                      onChange={(e) => {
                        setSelectedPersonIds((ids) =>
                          e.target.checked ? [...ids.filter((id) => id !== p.id), p.id] : ids.filter((id) => id !== p.id),
                        );
                      }}
                    >
                      {displayPersonNameWithEmail(p)}
                    </Checkbox>
                  ))}
                </CheckboxGroup>
                <Paging limit={limit} skip={skip} setSkip={setSkip} isLoading={isLoading} count={data.count} />
                {contactPersons.length > 0 && (
                  <Box my={4} py={4} borderTopWidth="1px" borderColor="smBorder">
                    <FormControl size="sm" pt="2">
                      <FormLabel fontSize="sm">{t("Contact persons registered by your organization")}</FormLabel>
                      <CheckboxGroup
                        colorScheme="teal"
                        value={selectedContactPersonEmails}
                        onChange={(emails) => setSelectedContactPersonEmails(emails as string[])}
                      >
                        {contactPersons.map((p) => (
                          <Checkbox key={p.email} value={p.email}>
                            {`${p.name ?? ""} (${p.email})`}
                          </Checkbox>
                        ))}
                      </CheckboxGroup>
                    </FormControl>
                  </Box>
                )}
                <Box borderTopWidth="1px" borderBottomWidth="1px" borderColor="smBorder">
                  <FormControl size="sm" pt="2">
                    <FormLabel fontSize="sm">{t("Invite additional users by email")}</FormLabel>
                    {manualEmailInvites.map((invite) => (
                      <Flex key={invite.id} mb={2} alignItems="center">
                        <Input
                          size="sm"
                          rounded="md"
                          value={invite.email}
                          isInvalid={invite.email.trim() !== "" && !isValidEmail(invite.email.trim())}
                          onChange={(e) =>
                            setManualEmailInvites((invites) =>
                              invites.map((item) => (item.id === invite.id ? { ...item, email: e.target.value } : item)),
                            )
                          }
                          mr={2}
                        />
                        <Select
                          size="sm"
                          rounded="md"
                          width="200px"
                          value={invite.language}
                          onChange={(e) =>
                            setManualEmailInvites((invites) =>
                              invites.map((item) =>
                                item.id === invite.id ? { ...item, language: e.target.value as EmailInviteLanguage } : item,
                              ),
                            )
                          }
                          mr={2}
                        >
                          <option value="No">{t("Norwegian")}</option>
                          <option value="En">{t("English")}</option>
                        </Select>
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => setManualEmailInvites((invites) => invites.filter((item) => item.id !== invite.id))}
                        >
                          {t("Remove")}
                        </Button>
                      </Flex>
                    ))}
                    <Button
                      size="sm"
                      mt={1}
                      onClick={() =>
                        setManualEmailInvites((invites) => [
                          ...invites,
                          { id: uuid(), email: "", language: EmailInviteLanguage.No },
                        ])
                      }
                    >
                      {t("Add email")}
                    </Button>
                    <FormHelperText fontSize="xs" py={2}>
                      {`${t("Only use this option if you want to invite users that are not listed above")}. ${t(
                        "One of the existing admins will have to grant the new users access to their organization",
                      )}.`}
                    </FormHelperText>
                  </FormControl>
                </Box>
                <FormControl flexDirection="column" alignItems="end" pt="5">
                  <Tooltip isDisabled={!isCreateDisabled} label={tooltipMessage} placement="top" hasArrow>
                    <Button isDisabled={isCreateDisabled} colorScheme="blue" onClick={createInvite}>
                      {t("Create invite")}
                    </Button>
                  </Tooltip>
                  {showPublishedWarning && (
                    <FormHelperText color="orange">
                      {t("The sourcing event is published and invitations will be sent at once")}.
                    </FormHelperText>
                  )}
                </FormControl>
              </Flex>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
